import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Phone } from 'lucide-react';
import { COMPANY } from '../data/mock';
import { brandConfig } from '../brandConfig';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [open, setOpen] = useState(false);
  const lastY = useRef(0);
  const loc = useLocation();

  useEffect(() => {
    const fn = () => {
      const y = window.scrollY;
      setScrolled(y > 60);
      // Hide navbar when scrolling down, show when scrolling up — works on all screen sizes
      if (y > lastY.current + 5 && y > 100) {
        setHidden(true);
        setOpen(false); // close mobile menu when hiding navbar
      }
      if (y < lastY.current) setHidden(false);
      lastY.current = y;
    };
    window.addEventListener('scroll', fn, { passive: true });
    return () => window.removeEventListener('scroll', fn);
  }, []);

  useEffect(() => setOpen(false), [loc]);

  const isHome = loc.pathname === '/';
  const links = [
    { to: '/packages', label: 'Packages', ar: 'الباقات' },
    { to: '/services', label: 'Services', ar: 'الخدمات' },
    { to: '/gallery', label: 'Gallery', ar: 'معرض' },
    { to: '/about', label: 'About', ar: 'عنا' },
    { to: '/contact', label: 'Contact', ar: 'اتصل' },
  ];

  const navBg = scrolled || !isHome ? 'rgba(10,22,40,0.97)' : 'transparent';

  return (
    <>
      <motion.header
        animate={{ y: hidden ? '-100%' : '0%' }}
        transition={{ duration: 0.35, ease: [0.76, 0, 0.24, 1] }}
        style={{
          position: 'fixed', top: 0, left: 0, right: 0, height: 76, zIndex: 1000,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '0 var(--gutter)',
          background: navBg,
          backdropFilter: scrolled || !isHome ? 'blur(20px)' : 'none',
          borderBottom: scrolled || !isHome ? '1px solid rgba(201,168,76,0.2)' : 'none',
          transition: 'background 0.4s, border 0.4s, backdrop-filter 0.4s',
        }}
      >
        <Link to="/" style={{ textDecoration: 'none' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <img src="/logo.png" alt={brandConfig.companyName} style={{ height: 56, width: 'auto', objectFit: 'contain' }} />
          </div>
        </Link>

        <nav className="desktop-nav" style={{ display: 'flex', gap: 36 }}>
          {links.map((l) => (
            <Link key={l.to} to={l.to} style={{
              fontFamily: 'Inter, sans-serif', fontSize: 13, fontWeight: 500,
              letterSpacing: '0.08em', textTransform: 'uppercase',
              color: loc.pathname === l.to ? '#C9A84C' : '#FBF8F0',
              textDecoration: 'none',
              borderBottom: loc.pathname === l.to ? '1px solid #C9A84C' : '1px solid transparent',
              paddingBottom: 4, transition: 'color 0.2s, border 0.2s',
            }}>
              {l.label}
            </Link>
          ))}
        </nav>

        <div className="navbar-right" style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <div className="hours-badge" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{
              width: 7, height: 7, borderRadius: '50%', background: '#22C55E',
              boxShadow: '0 0 0 3px rgba(34,197,94,0.25)',
              animation: 'pulse 2s infinite',
            }} />
            <span style={{
              fontFamily: 'Inter', fontSize: 11, fontWeight: 500, color: '#FBF8F0',
              letterSpacing: '0.12em', textTransform: 'uppercase',
            }}>24/7 Open</span>
          </div>

          <Link to="/contact" style={{ textDecoration: 'none' }}>
            <motion.button
              whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}
              style={{
                fontFamily: 'Inter', fontSize: 12, fontWeight: 600,
                letterSpacing: '0.1em', textTransform: 'uppercase',
                color: '#0A1628', background: '#C9A84C', border: 'none',
                padding: '12px 26px', cursor: 'pointer', borderRadius: 999,
              }}
            >Book Now</motion.button>
          </Link>
        </div>

        <button onClick={() => setOpen(!open)} className="mob-btn"
          style={{ display: 'none', background: 'none', border: 'none', cursor: 'pointer', padding: 8 }}>
          {open ? <X color="#C9A84C" size={26} /> : <Menu color="#C9A84C" size={26} />}
        </button>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, x: '100%' }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: '100%' }}
            transition={{ duration: 0.4, ease: [0.76, 0, 0.24, 1] }}
            style={{
              position: 'fixed', inset: 0, background: '#0A1628', zIndex: 999,
              display: 'flex', flexDirection: 'column', alignItems: 'center',
              justifyContent: 'center', gap: 32, padding: 32,
            }}
          >
            <img
              src="/logo.png"
              alt={brandConfig.companyName}
              style={{ height: 90, width: 'auto', objectFit: 'contain', marginBottom: 12 }}
            />
            {links.map((l, i) => (
              <motion.div key={l.to}
                initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.07 }}>
                <Link to={l.to} style={{
                  fontFamily: 'Amiri, serif', fontSize: 32, fontWeight: 400,
                  color: '#FBF8F0', textDecoration: 'none',
                }}>{l.label}</Link>
              </motion.div>
            ))}
            <div className="arabic-text" style={{ fontSize: 18, color: '#C9A84C', marginTop: 12 }}>
              {COMPANY.arabicName}
            </div>
            <a href={`tel:${COMPANY.phoneIntl}`} style={{
              fontFamily: 'Inter', fontSize: 18, fontWeight: 600,
              color: '#C9A84C', textDecoration: 'none',
              display: 'flex', alignItems: 'center', gap: 8,
            }}>
              <Phone size={20} /> {COMPANY.phoneDisplay}
            </a>
          </motion.div>
        )}
      </AnimatePresence>

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.6; }
        }
        @media (max-width: 1023px) {
          .desktop-nav { display: none !important; }
          .mob-btn { display: block !important; }
          .navbar-right { display: none !important; }
        }
        @media (max-width: 640px) {
          .hours-badge { display: none !important; }
        }
      `}</style>
    </>
  );
}
